import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { trackQuoteFormConversion } from '../../lib/gtagConversion';

interface QuoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  lang?: 'en' | 'tr';
}

// ... existing code ...

const LABELS = {
  en: {
    title: 'Request a Quote',
    subtitle: "We'll get back to you within 24 hours",
    successTitle: 'Request Sent!',
    successMsg: "Your quote request has been received. We'll reach out to you shortly.",
    close: 'Close',
    fullName: 'Full Name *',
    namePlaceholder: 'Your full name',
    emailAddress: 'Email Address *',
    eventType: 'Event Type *',
    selectEvent: 'Select event type...',
    startDate: 'Start Date *',
    endDate: 'End Date *',
    message: 'Message *',
    messageNote: '(Max 500 characters)',
    messagePlaceholder: 'Tell us about your event — location, expected guests, special requirements...',
    spamVerification: 'Spam Verification',
    refresh: 'Refresh',
    answer: 'Answer',
    wrongAnswer: 'Wrong answer, try again.',
    cancel: 'Cancel',
    send: 'Send Request',
    sending: 'Sending...',
    fillAll: 'Please fill in all required fields.',
    endBeforeStart: 'End date cannot be before start date.',
    msgTooLong: 'Message cannot exceed 500 characters.',
    captchaFail: 'Please complete the captcha verification.',
    eventOptions: [
      { value: 'Corporate Conference', label: 'Corporate Conference' },
      { value: 'Product Launch', label: 'Product Launch' },
      { value: 'Gala Dinner & Award Ceremony', label: 'Gala Dinner & Award Ceremony' },
      { value: 'Incentive Trip & Team Building', label: 'Incentive Trip & Team Building' },
      { value: 'Exhibition & Trade Show', label: 'Exhibition & Trade Show' },
      { value: 'Concert & Entertainment', label: 'Concert & Entertainment' },
      { value: 'Technical Production & AV', label: 'Technical Production & AV' },
      { value: 'Hybrid & Virtual Event', label: 'Hybrid & Virtual Event' },
      { value: 'Other', label: 'Other' },
    ],
  },
  tr: {
    title: 'Teklif Talep Et',
    subtitle: '24 saat içinde size geri döneceğiz',
    successTitle: 'Talep Gönderildi!',
    successMsg: 'Teklif talebiniz alındı. En kısa sürede sizinle iletişime geçeceğiz.',
    close: 'Kapat',
    fullName: 'Ad Soyad *',
    namePlaceholder: 'Adınız ve soyadınız',
    emailAddress: 'E-posta Adresi *',
    eventType: 'Etkinlik Türü *',
    selectEvent: 'Etkinlik türünü seçin...',
    startDate: 'Başlangıç Tarihi *',
    endDate: 'Bitiş Tarihi *',
    message: 'Mesaj *',
    messageNote: '(Maks. 500 karakter)',
    messagePlaceholder: 'Etkinliğiniz hakkında bilgi verin — konum, beklenen konuk sayısı, özel gereksinimler...',
    spamVerification: 'Spam Doğrulama',
    refresh: 'Yenile',
    answer: 'Cevap',
    wrongAnswer: 'Yanlış cevap, tekrar deneyin.',
    cancel: 'İptal',
    send: 'Talebi Gönder',
    sending: 'Gönderiliyor...',
    fillAll: 'Lütfen tüm zorunlu alanları doldurun.',
    endBeforeStart: 'Bitiş tarihi başlangıç tarihinden önce olamaz.',
    msgTooLong: 'Mesaj 500 karakteri geçemez.',
    captchaFail: 'Lütfen captcha doğrulamasını tamamlayın.',
    eventOptions: [
      { value: 'Corporate Conference', label: 'Kurumsal Konferans' },
      { value: 'Product Launch', label: 'Ürün Lansmanı' },
      { value: 'Gala Dinner & Award Ceremony', label: 'Gala Yemeği & Ödül Töreni' },
      { value: 'Incentive Trip & Team Building', label: 'Motivasyon Gezisi & Takım Çalışması' },
      { value: 'Exhibition & Trade Show', label: 'Sergi & Ticaret Fuarı' },
      { value: 'Concert & Entertainment', label: 'Konser & Eğlence' },
      { value: 'Technical Production & AV', label: 'Teknik Prodüksiyon & AV' },
      { value: 'Hybrid & Virtual Event', label: 'Hibrit & Sanal Etkinlik' },
      { value: 'Other', label: 'Diğer' },
    ],
  },
};

interface FormState {
  name: string;
  email: string;
  subject: string;
  message: string;
  start_date: string;
  end_date: string;
}

interface CaptchaState {
  num1: number;
  num2: number;
  answer: string;
  verified: boolean;
}

function generateCaptcha(): { num1: number; num2: number } {
  return {
    num1: Math.floor(Math.random() * 9) + 1,
    num2: Math.floor(Math.random() * 9) + 1,
  };
}

export default function QuoteModal({ isOpen, onClose, lang = 'en' }: QuoteModalProps) {
  const t = LABELS[lang];
  const [form, setForm] = useState<FormState>({
    name: '', email: '', subject: '', message: '', start_date: '', end_date: '',
  });
  const [captcha, setCaptcha] = useState<CaptchaState>({ ...generateCaptcha(), answer: '', verified: false });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (isOpen) {
      const nums = generateCaptcha();
      setCaptcha({ ...nums, answer: '', verified: false });
      setStatus('idle');
      setErrorMsg('');
    }
  }, [isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleCaptchaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    const correct = captcha.num1 + captcha.num2;
    setCaptcha(prev => ({ ...prev, answer: val, verified: parseInt(val) === correct }));
  };

  const refreshCaptcha = () => {
    const nums = generateCaptcha();
    setCaptcha({ ...nums, answer: '', verified: false });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const { name, email, subject, message, start_date, end_date } = form;
    if (!name.trim() || !email.trim() || !subject.trim() || !message.trim() || !start_date || !end_date) {
      setStatus('error'); setErrorMsg(t.fillAll); return;
    }
    if (new Date(end_date) < new Date(start_date)) {
      setStatus('error'); setErrorMsg(t.endBeforeStart); return;
    }
    if (message.length > 500) {
      setStatus('error'); setErrorMsg(t.msgTooLong); return;
    }
    if (!captcha.verified) {
      setStatus('error'); setErrorMsg(t.captchaFail); return;
    }
    setIsSubmitting(true); setStatus('idle'); setErrorMsg('');
    try {
      const { error } = await supabase
        .from('contact_requests')
        .insert({ name, email, subject, message, start_date, end_date });
      if (error) { setStatus('error'); setErrorMsg(error.message || 'An error occurred.'); return; }
      setStatus('success');
      trackQuoteFormConversion();
      setForm({ name: '', email: '', subject: '', message: '', start_date: '', end_date: '' });
    } catch (err: unknown) {
      setStatus('error');
      setErrorMsg(err instanceof Error ? err.message : 'Unexpected error.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => { setStatus('idle'); setErrorMsg(''); onClose(); };
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gray-950 border border-gray-800 rounded-2xl max-w-lg w-full max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-800">
          <div>
            <h3 className="text-xl font-semibold text-white">{t.title}</h3>
            <p className="text-sm text-gray-400 mt-1">{t.subtitle}</p>
          </div>
          <button onClick={handleClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors cursor-pointer">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>
        <div className="p-6">
          {status === 'success' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-500/10 border border-green-500/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-check-line text-2xl text-green-400"></i>
              </div>
              <h4 className="text-lg font-semibold text-white mb-2">{t.successTitle}</h4>
              <p className="text-gray-400 text-sm mb-6">{t.successMsg}</p>
              <button onClick={handleClose} className="px-6 py-2 bg-yellow-400 text-black rounded-lg font-medium hover:bg-yellow-300 transition-colors cursor-pointer whitespace-nowrap">
                {t.close}
              </button>
            </div>
          )}
          {status !== 'success' && (
            <form onSubmit={handleSubmit} className="space-y-4">
              {status === 'error' && (
                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <p className="text-red-400 text-sm"><i className="ri-error-warning-line mr-2"></i>{errorMsg}</p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">{t.fullName}</label>
                <input type="text" name="name" value={form.name} onChange={handleChange} placeholder={t.namePlaceholder}
                  className="w-full px-3 py-2.5 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-yellow-400 transition-colors" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">{t.emailAddress}</label>
                <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="your@email.com"
                  className="w-full px-3 py-2.5 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-yellow-400 transition-colors" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">{t.eventType}</label>
                <select name="subject" value={form.subject} onChange={handleChange}
                  className="w-full px-3 py-2.5 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-yellow-400 transition-colors" required>
                  <option value="">{t.selectEvent}</option>
                  {t.eventOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">
                    <i className="ri-calendar-line mr-1 text-yellow-400"></i>{t.startDate}
                  </label>
                  <input type="date" name="start_date" value={form.start_date} onChange={handleChange}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2.5 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-yellow-400 transition-colors [color-scheme:dark]" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">
                    <i className="ri-calendar-check-line mr-1 text-yellow-400"></i>{t.endDate}
                  </label>
                  <input type="date" name="end_date" value={form.end_date} onChange={handleChange}
                    min={form.start_date || new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2.5 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-yellow-400 transition-colors [color-scheme:dark]" required />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">
                  {t.message} <span className="text-gray-500 font-normal">{t.messageNote}</span>
                </label>
                <textarea name="message" value={form.message} onChange={handleChange} placeholder={t.messagePlaceholder}
                  rows={4} maxLength={500}
                  className="w-full px-3 py-2.5 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-yellow-400 transition-colors resize-none" required />
                <div className="text-right text-xs text-gray-500 mt-1">{form.message.length}/500</div>
              </div>
              <div className="bg-gray-900 border border-gray-700 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-300">
                    <i className="ri-shield-check-line mr-2 text-yellow-400"></i>{t.spamVerification}
                  </span>
                  <button type="button" onClick={refreshCaptcha}
                    className="text-xs text-gray-500 hover:text-yellow-400 transition-colors cursor-pointer flex items-center gap-1">
                    <i className="ri-refresh-line"></i> {t.refresh}
                  </button>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 bg-gray-800 border border-gray-600 rounded-lg px-4 py-2.5 text-center min-w-28">
                    <span className="text-yellow-400 font-bold text-lg tracking-widest">{captcha.num1} + {captcha.num2} = ?</span>
                  </div>
                  <div className="relative flex-1">
                    <input type="number" value={captcha.answer} onChange={handleCaptchaChange} placeholder={t.answer}
                      className={`w-full px-3 py-2.5 bg-gray-800 border rounded-lg text-white text-sm focus:outline-none transition-colors ${captcha.verified ? 'border-green-500 focus:border-green-400' : 'border-gray-600 focus:border-yellow-400'}`} />
                    {captcha.verified && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <i className="ri-check-line text-green-400"></i>
                      </div>
                    )}
                  </div>
                </div>
                {captcha.answer && !captcha.verified && (
                  <p className="text-red-400 text-xs mt-2"><i className="ri-close-line mr-1"></i>{t.wrongAnswer}</p>
                )}
              </div>
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={handleClose}
                  className="flex-1 px-4 py-2.5 border border-gray-700 text-gray-300 rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer text-sm">
                  {t.cancel}
                </button>
                <button type="submit" disabled={isSubmitting || !captcha.verified}
                  className="flex-1 px-4 py-2.5 bg-yellow-400 text-black rounded-lg font-semibold hover:bg-yellow-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer text-sm">
                  {isSubmitting
                    ? <><i className="ri-loader-4-line animate-spin mr-2"></i>{t.sending}</>
                    : <><i className="ri-send-plane-line mr-2"></i>{t.send}</>
                  }
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
